import type {
  SalesOfferChannel,
  SalesOfferRecord,
  SalesOpportunityRecord,
  SalesOpportunityStage,
  SalesPositionKind,
  SalesSource,
} from './types';

/**
 * Vertriebs-Controlling -- reine Aggregation über die echten Verkaufsdaten (Chancen + Angebote).
 * Bewusst als pure Funktion (kein Gateway/keine Migration): die Auswertungsansicht lädt Chancen und
 * Angebote und rechnet hier. So bleibt die Logik testbar und Demo == Supabase.
 *
 * „Umsatz" = Netto der GEWONNENEN Angebote (status 'won'); „Marge" = deren Deckungsbeitrag.
 */

/** Reihenfolge der OFFENEN Pipeline-Phasen (won/lost sind Abschlüsse, nicht Teil des Trichters). */
export const openStageOrder: readonly SalesOpportunityStage[] = [
  'new', 'qualification', 'needs-confirmed', 'offer-planned', 'offer-presented', 'follow-up',
];

export const opportunityStageLabels: Readonly<Record<SalesOpportunityStage, string>> = {
  'new': 'Neu',
  'qualification': 'Qualifizierung',
  'needs-confirmed': 'Bedarf bestätigt',
  'offer-planned': 'Angebot geplant',
  'offer-presented': 'Angebot vorgelegt',
  'follow-up': 'Nachfassen',
  'won': 'Gewonnen',
  'lost': 'Verloren',
};

export const salesSourceLabels: Readonly<Record<SalesSource, string>> = {
  'phone': 'Telefon',
  'email': 'E-Mail',
  'website': 'Website',
  'referral': 'Empfehlung',
  'marketplace': 'Marktplatz',
  'existing-customer': 'Bestandskunde',
  'fair': 'Messe',
  'other': 'Sonstige',
};

export const offerChannelLabels: Readonly<Record<SalesOfferChannel, string>> = {
  'phone': 'Telefon',
  'email': 'E-Mail',
  'document': 'Schriftlich',
  'onsite': 'Vor Ort',
};

export interface SalesFunnelStage {
  readonly stage: SalesOpportunityStage;
  readonly label: string;
  readonly count: number;
  readonly expectedValue: number;
  readonly weightedValue: number;
}

export interface SalesMonthlyPoint {
  /** `YYYY-MM`. */
  readonly month: string;
  /** „Jul 26". */
  readonly label: string;
  readonly wonCount: number;
  readonly wonValue: number;
}

export interface SalesChannelPoint {
  readonly channel: SalesOfferChannel;
  readonly label: string;
  readonly wonCount: number;
  readonly wonValue: number;
}

export interface SalesSourcePoint {
  readonly source: SalesSource;
  readonly label: string;
  readonly wonCount: number;
  readonly wonValue: number;
}

/** Gewonnener Umsatz + Deckungsbeitrag je Positions-Art (Warengruppe: Maschine/Artikel/Service). */
export interface SalesPositionKindPoint {
  readonly kind: SalesPositionKind;
  readonly wonValue: number;
  readonly margin: number;
  readonly count: number;
}

export interface SalesTopCustomer {
  readonly customerNumber: string;
  readonly customerName: string;
  readonly wonCount: number;
  readonly wonValue: number;
}

/**
 * Vorsichtige Vorschau, KEINE Zusage: die gewichtete Pipeline (Σ erwarteter Wert × Wahrscheinlichkeit)
 * und eine Run-Rate aus den letzten drei Monaten. Bewusst zwei Blickwinkel statt einer Scheingenauigkeit.
 */
export interface SalesForecast {
  /** Erwarteter Umsatz aus den offenen Chancen (Σ weightedValue). */
  readonly weightedPipeline: number;
  /** Ø gewonnener Umsatz je Monat über die letzten (bis zu) drei Monate. */
  readonly runRateMonthly: number;
  /** Run-Rate auf ein Quartal hochgerechnet. */
  readonly runRateQuarter: number;
}

export interface SalesAnalytics {
  // Pipeline (offene Chancen)
  readonly openOpportunityCount: number;
  readonly pipelineValue: number;
  readonly weightedPipelineValue: number;
  readonly funnel: readonly SalesFunnelStage[];
  // Abschlüsse (Angebote)
  readonly wonCount: number;
  readonly wonValue: number;
  readonly wonMargin: number;
  readonly lostCount: number;
  readonly lostValue: number;
  readonly decidedCount: number;
  /** Gewonnen / (gewonnen + verloren), 0..1. */
  readonly winRate: number;
  readonly avgDealSize: number;
  // Offene Angebote
  readonly openOfferCount: number;
  readonly openOfferValue: number;
  // Zeitreihe + Aufschlüsselungen
  readonly monthly: readonly SalesMonthlyPoint[];
  readonly byChannel: readonly SalesChannelPoint[];
  readonly bySource: readonly SalesSourcePoint[];
  readonly byPositionKind: readonly SalesPositionKindPoint[];
  readonly topCustomers: readonly SalesTopCustomer[];
  readonly forecast: SalesForecast;
}

const OPEN_OFFER_STATUS = new Set(['draft', 'presented', 'follow-up']);
const MONTH_ABBR = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

function monthKey(iso: string): string {
  return iso.slice(0, 7);
}
function monthLabel(key: string): string {
  const [year, month] = key.split('-');
  const index = Number(month) - 1;
  return `${MONTH_ABBR[index] ?? month} ${year.slice(2)}`;
}

/** Die letzten `count` Monatsschlüssel bis einschließlich `todayIso` (ältester zuerst). */
function recentMonths(todayIso: string, count: number): string[] {
  let year = Number(todayIso.slice(0, 4));
  let month = Number(todayIso.slice(5, 7)); // 1..12
  const keys: string[] = [];
  for (let i = 0; i < count; i += 1) {
    keys.push(`${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}`);
    month -= 1;
    if (month === 0) { month = 12; year -= 1; }
  }
  return keys.reverse();
}

export type SalesControllingPeriod = 'last12' | 'ytd' | 'quarter' | 'all';

export const salesPeriodOrder: readonly SalesControllingPeriod[] = ['last12', 'ytd', 'quarter', 'all'];

export const salesPeriodLabels: Readonly<Record<SalesControllingPeriod, string>> = {
  last12: 'Letzte 12 Monate',
  ytd: 'Dieses Jahr',
  quarter: 'Dieses Quartal',
  all: 'Alles',
};

export interface SalesPeriodRange {
  /** Frühestes Abschlussdatum (YYYY-MM-DD), das noch zählt; null = keine Untergrenze. */
  readonly from: string | null;
  /** Spätestes Abschlussdatum (YYYY-MM-DD). */
  readonly to: string;
  /** Monatsschlüssel für die Trend-Balken (ältester zuerst). */
  readonly months: readonly string[];
}

/** Zeitfenster + Trend-Monate für eine Periode. Gewonnen/verloren zählen nach Abschlussdatum. */
export function salesPeriodRange(period: SalesControllingPeriod, todayIso: string): SalesPeriodRange {
  const year = Number(todayIso.slice(0, 4));
  const month = Number(todayIso.slice(5, 7)); // 1..12
  const to = todayIso.slice(0, 10);
  const monthsUpTo = (startMonth: number): string[] => {
    const keys: string[] = [];
    for (let m = startMonth; m <= month; m += 1) keys.push(`${String(year).padStart(4, '0')}-${String(m).padStart(2, '0')}`);
    return keys;
  };
  if (period === 'ytd') {
    return { from: `${year}-01-01`, to, months: monthsUpTo(1) };
  }
  if (period === 'quarter') {
    const quarterStart = Math.floor((month - 1) / 3) * 3 + 1;
    return { from: `${String(year).padStart(4, '0')}-${String(quarterStart).padStart(2, '0')}-01`, to, months: monthsUpTo(quarterStart) };
  }
  // last12 + all: 12-Monats-Fenster für den Trend; 'all' ohne Startgrenze für die Kennzahlen.
  const months = recentMonths(todayIso, 12);
  const from = period === 'all' ? null : `${months[0]}-01`;
  return { from, to, months };
}

/**
 * Kern der Auswertung über eine bereits aufgelöste Zeit-Range ({from,to,months}). So kann der
 * Auswertung-Hub mit seinem eigenen Zeitraumfilter (inkl. freiem Von/Bis) dieselbe Logik nutzen.
 */
export function computeSalesAnalyticsForRange(
  opportunities: readonly SalesOpportunityRecord[],
  offers: readonly SalesOfferRecord[],
  range: SalesPeriodRange,
): SalesAnalytics {
  // Abschlüsse (gewonnen/verloren) zählen nach Abschlussdatum im gewählten Zeitfenster.
  const inPeriod = (offer: SalesOfferRecord): boolean => {
    const date = (offer.decidedAt ?? offer.createdAt).slice(0, 10);
    return (range.from === null || date >= range.from) && date <= range.to;
  };
  const open = opportunities.filter((o) => o.stage !== 'won' && o.stage !== 'lost');
  const pipelineValue = open.reduce((sum, o) => sum + o.expectedValue, 0);
  const weightedPipelineValue = open.reduce((sum, o) => sum + o.weightedValue, 0);

  const funnel: SalesFunnelStage[] = openStageOrder.map((stage) => {
    const rows = open.filter((o) => o.stage === stage);
    return {
      stage,
      label: opportunityStageLabels[stage],
      count: rows.length,
      expectedValue: rows.reduce((sum, o) => sum + o.expectedValue, 0),
      weightedValue: rows.reduce((sum, o) => sum + o.weightedValue, 0),
    };
  });

  const wonOffers = offers.filter((offer) => offer.status === 'won' && inPeriod(offer));
  const lostOffers = offers.filter((offer) => offer.status === 'lost' && inPeriod(offer));
  const wonValue = wonOffers.reduce((sum, offer) => sum + offer.netTotal, 0);
  const wonMargin = wonOffers.reduce((sum, offer) => sum + offer.contributionMargin, 0);
  const lostValue = lostOffers.reduce((sum, offer) => sum + offer.netTotal, 0);
  const decidedCount = wonOffers.length + lostOffers.length;
  const winRate = decidedCount > 0 ? wonOffers.length / decidedCount : 0;
  const avgDealSize = wonOffers.length > 0 ? wonValue / wonOffers.length : 0;

  const openOffers = offers.filter((offer) => OPEN_OFFER_STATUS.has(offer.status));
  const openOfferValue = openOffers.reduce((sum, offer) => sum + offer.netTotal, 0);

  // Zeitreihe der gewonnenen Angebote nach Entscheidungsmonat (Rückfall: Erstelldatum).
  const monthTotals = new Map<string, { wonCount: number; wonValue: number }>();
  for (const offer of wonOffers) {
    const key = monthKey(offer.decidedAt ?? offer.createdAt);
    const bucket = monthTotals.get(key) ?? { wonCount: 0, wonValue: 0 };
    bucket.wonCount += 1;
    bucket.wonValue += offer.netTotal;
    monthTotals.set(key, bucket);
  }
  const monthly: SalesMonthlyPoint[] = range.months.map((key) => ({
    month: key,
    label: monthLabel(key),
    wonCount: monthTotals.get(key)?.wonCount ?? 0,
    wonValue: monthTotals.get(key)?.wonValue ?? 0,
  }));

  const channelTotals = new Map<SalesOfferChannel, { wonCount: number; wonValue: number }>();
  for (const offer of wonOffers) {
    const bucket = channelTotals.get(offer.channel) ?? { wonCount: 0, wonValue: 0 };
    bucket.wonCount += 1;
    bucket.wonValue += offer.netTotal;
    channelTotals.set(offer.channel, bucket);
  }
  const byChannel: SalesChannelPoint[] = [...channelTotals.entries()]
    .map(([channel, totals]) => ({ channel, label: offerChannelLabels[channel], ...totals }))
    .sort((left, right) => right.wonValue - left.wonValue);

  // Umsatz nach Quelle: das Angebot kennt keine Quelle -- sie steht an der Verkaufschance. Über
  // opportunityId zuordnen; Angebote ohne verknüpfte Chance zählen als „Sonstige".
  const opportunityById = new Map(opportunities.map((o) => [o.id, o] as const));
  const sourceTotals = new Map<SalesSource, { wonCount: number; wonValue: number }>();
  for (const offer of wonOffers) {
    const source = (offer.opportunityId ? opportunityById.get(offer.opportunityId)?.source : undefined) ?? 'other';
    const bucket = sourceTotals.get(source) ?? { wonCount: 0, wonValue: 0 };
    bucket.wonCount += 1;
    bucket.wonValue += offer.netTotal;
    sourceTotals.set(source, bucket);
  }
  const bySource: SalesSourcePoint[] = [...sourceTotals.entries()]
    .map(([source, totals]) => ({ source, label: salesSourceLabels[source], ...totals }))
    .sort((left, right) => right.wonValue - left.wonValue);

  // Umsatz + Deckungsbeitrag je Positions-Art (Warengruppe) aus den gewonnenen Angeboten.
  const positionKindTotals = new Map<SalesPositionKind, { wonValue: number; margin: number; count: number }>();
  for (const offer of wonOffers) {
    for (const position of offer.positions) {
      if (position.kind === 'text') continue;
      const bucket = positionKindTotals.get(position.kind) ?? { wonValue: 0, margin: 0, count: 0 };
      bucket.wonValue += position.netAmount;
      bucket.margin += position.contributionMargin;
      bucket.count += 1;
      positionKindTotals.set(position.kind, bucket);
    }
  }
  const byPositionKind: SalesPositionKindPoint[] = [...positionKindTotals.entries()]
    .map(([kind, totals]) => ({ kind, ...totals }))
    .sort((left, right) => right.wonValue - left.wonValue);

  // Prognose: gewichtete Pipeline + Run-Rate der letzten (bis zu) drei Monate.
  const recentMonthly = monthly.slice(-3);
  const runRateMonthly = recentMonthly.length > 0
    ? recentMonthly.reduce((sum, point) => sum + point.wonValue, 0) / recentMonthly.length
    : 0;
  const forecast: SalesForecast = {
    weightedPipeline: weightedPipelineValue,
    runRateMonthly,
    runRateQuarter: runRateMonthly * 3,
  };

  const customerTotals = new Map<string, SalesTopCustomer>();
  for (const offer of wonOffers) {
    const key = offer.customerNumber || offer.customerName || offer.id;
    const bucket = customerTotals.get(key)
      ?? { customerNumber: offer.customerNumber, customerName: offer.customerName || offer.prospectName || 'Ohne Kunde', wonCount: 0, wonValue: 0 };
    customerTotals.set(key, { ...bucket, wonCount: bucket.wonCount + 1, wonValue: bucket.wonValue + offer.netTotal });
  }
  const topCustomers: SalesTopCustomer[] = [...customerTotals.values()]
    .sort((left, right) => right.wonValue - left.wonValue)
    .slice(0, 8);

  return {
    openOpportunityCount: open.length,
    pipelineValue,
    weightedPipelineValue,
    funnel,
    wonCount: wonOffers.length,
    wonValue,
    wonMargin,
    lostCount: lostOffers.length,
    lostValue,
    decidedCount,
    winRate,
    avgDealSize,
    openOfferCount: openOffers.length,
    openOfferValue,
    monthly,
    byChannel,
    bySource,
    byPositionKind,
    topCustomers,
    forecast,
  };
}

/** Bequeme Variante mit Preset-Zeitraum (löst intern in eine Range auf). */
export function computeSalesAnalytics(
  opportunities: readonly SalesOpportunityRecord[],
  offers: readonly SalesOfferRecord[],
  todayIso: string,
  period: SalesControllingPeriod = 'last12',
): SalesAnalytics {
  return computeSalesAnalyticsForRange(opportunities, offers, salesPeriodRange(period, todayIso));
}
